import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { radius } from '@karehero/llama/theme/radius';

import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import StatusBar from '@/designs-dev/device/StatusBar';
import { northStarTheme } from '@/north-star/theme';
import KeniOrb from '@/components/wrapped/KeniOrb';
import ChatBubble from '@/north-star/ChatBubble';
import BenefitRow from '@/north-star/BenefitRow';
import LandingScreen from '@/north-star/LandingScreen';
import { speak } from '@/north-star/speak';

import Collection from '@/benefits-flow/Collection';
import {
  buildInput,
  optionsFor,
  QUESTIONS,
  QUIZ_ORDER,
  RECIPIENTS,
  visibleQuestions,
  type Answers,
  type AnswerValue,
  type Question,
  type Recipient,
} from '@/benefits-flow/screener/questions';
import { BENEFITS } from '@/benefits-flow/engine/benefits';
import { valueRange, gbp } from '@/benefits-flow/format';
import { byValueDesc, eligibleResults } from '@/benefits-flow/reveal/selectors';
import { evaluate, totalEstimatedValue } from '@/benefits-flow/engine/evaluate';
import { ACTIONS } from '@/benefits-flow/BenefitsFlow.content';
import type { FoundBenefit } from '@/north-star/NorthStar.fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// The benefits logic, shown through Keni. Same warm conversational journey as
// the North Star — orb, chat bubbles, the reveal card — but the questions are
// the real (minimal, conditional) decision tree and the reveal is computed by
// the eligibility engine, not scripted. Benefits light up in the conversation
// as Keni works them out. The original North Star journey is left untouched.

type Msg = { from: 'keni' | 'user'; text: string };
type Phase = 'landing' | 'chat' | 'reveal' | 'collect';

const RECIPIENT_STEP = '__recipient__';

const withName = (text: string, name: string) =>
  text.replaceAll('{name}', name);

// The first still-unanswered visible question, given answers so far.
const nextQuestion = (answers: Answers): Question | undefined => {
  const visible = visibleQuestions(answers, QUIZ_ORDER);
  return visible.find((q) => answers[q.id] === undefined);
};

const eligibleIds = (answers: Answers): string[] =>
  evaluate(buildInput(answers, QUIZ_ORDER)).eligibleIds;

// Keni's line when a benefit newly unlocks mid-conversation.
const unlockLine = (benefitId: string, name: string): string => {
  const info = BENEFITS[benefitId];
  if (!info) return '';
  const range = valueRange(info.min, info.max);
  return range
    ? `Good news — ${name} could get ${info.shortName}, up to ${range} a year.`
    : `Good news — ${name} could get ${info.shortName}.`;
};

const KeniBenefitsJourney = () => {
  const [phase, setPhase] = useState<Phase>('landing');
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [messages, setMessages] = useState<Msg[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [multiSel, setMultiSel] = useState<string[]>([]);

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, phase]);

  const name = recipient?.name ?? 'the person you care for';
  const evaluation = evaluate(buildInput(answers, QUIZ_ORDER));

  const say = (msgs: Msg[]) => setMessages((prev) => [...prev, ...msgs]);

  const start = () => {
    setRecipient(null);
    setAnswers({});
    setMultiSel([]);
    setMessages([
      {
        from: 'keni',
        text: 'Let’s see what you could claim. First — who are you caring for?',
      },
    ]);
    setCurrentId(RECIPIENT_STEP);
    setPhase('chat');
  };

  // Move the conversation to the next question, or wrap up into the reveal.
  const askNext = (nextAnswers: Answers, who: string) => {
    const q = nextQuestion(nextAnswers);
    if (q) {
      say([{ from: 'keni', text: withName(q.prompt, who) }]);
      setCurrentId(q.id);
      setMultiSel([]);
    } else {
      say([
        {
          from: 'keni',
          text: `That’s everything I need. Here’s what ${who} could claim.`,
        },
      ]);
      setCurrentId(null);
    }
  };

  const pickRecipient = (r: Recipient) => {
    setRecipient(r);
    say([{ from: 'user', text: r.label }]);
    askNext({}, r.name);
  };

  const answer = (q: Question, value: AnswerValue, label: string) => {
    const nextAnswers = { ...answers, [q.id]: value };
    const before = new Set(eligibleIds(answers));
    const after = eligibleIds(nextAnswers);
    const fresh = after.filter((id) => !before.has(id));

    const reactions: Msg[] = [];
    if (fresh.length === 1) {
      reactions.push({ from: 'keni', text: unlockLine(fresh[0], name) });
    } else if (fresh.length > 1) {
      reactions.push({
        from: 'keni',
        text: `That opens up ${fresh.length} more — I’ll show you at the end.`,
      });
    }

    say([{ from: 'user', text: label }, ...reactions]);
    setAnswers(nextAnswers);
    askNext(nextAnswers, name);
  };

  // ── Landing ──
  if (phase === 'landing') {
    return (
      <ThemeProvider theme={northStarTheme}>
        <PhoneFrame>
          <LandingScreen onStart={start} />
        </PhoneFrame>
      </ThemeProvider>
    );
  }

  const currentQuestion =
    currentId && currentId !== RECIPIENT_STEP
      ? QUESTIONS[currentId]
      : undefined;

  // The quick-reply chips for the current question.
  const renderChips = () => {
    if (currentId === RECIPIENT_STEP) {
      return RECIPIENTS.map((r) => (
        <Chip
          key={r.id}
          label={r.label}
          onClick={() => pickRecipient(r)}
          clickable
        />
      ));
    }
    const q = currentQuestion;
    if (!q) return null;
    const options = optionsFor(q, buildInput(answers, QUIZ_ORDER).nation);

    if (q.type === 'boolean') {
      return (
        <>
          <Chip label='Yes' clickable onClick={() => answer(q, true, 'Yes')} />
          <Chip label='No' clickable onClick={() => answer(q, false, 'No')} />
        </>
      );
    }

    if (q.type === 'multi-select') {
      const noneId = q.options?.find((o) => o.value === 'none')?.id;
      const toggle = (id: string) => {
        if (id === noneId) {
          setMultiSel([id]);
          return;
        }
        setMultiSel((prev) =>
          prev.includes(id)
            ? prev.filter((s) => s !== id)
            : [...prev.filter((s) => s !== noneId), id],
        );
      };
      return (
        <>
          {options.map((o) => (
            <Chip
              key={o.id}
              label={o.label}
              clickable
              color={multiSel.includes(o.id) ? 'primary' : 'default'}
              variant={multiSel.includes(o.id) ? 'filled' : 'outlined'}
              onClick={() => toggle(o.id)}
            />
          ))}
          {multiSel.length > 0 && (
            <Chip
              label='Done'
              clickable
              color='primary'
              onClick={() => {
                const labels = multiSel
                  .map((id) => q.options?.find((o) => o.id === id)?.label)
                  .filter(Boolean)
                  .join(', ');
                answer(q, multiSel, labels || 'None');
              }}
            />
          )}
        </>
      );
    }

    // choice
    return options.map((o) => (
      <Chip
        key={o.id}
        label={o.label}
        clickable
        onClick={() => answer(q, o.id, o.label)}
      />
    ));
  };

  // ── Reveal ──
  if (phase === 'reveal') {
    const rows: FoundBenefit[] = byValueDesc(eligibleResults(evaluation)).map(
      (r) => {
        const info = BENEFITS[r.benefitId];
        const range = valueRange(info?.min ?? null, info?.max ?? null);
        return {
          name: info?.shortName ?? r.benefitId,
          annual: range ?? '—',
          status: ACTIONS[r.action].label,
          tone: r.action === 'APPLY_NOW' ? 'good' : 'maybe',
        };
      },
    );
    const total = totalEstimatedValue(evaluation);

    return (
      <ThemeProvider theme={northStarTheme}>
        <PhoneFrame>
          <ScreenRoot>
            <StatusBar />
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: '24px' }}>
              <Stack spacing={3}>
                <Typography sx={{ textAlign: 'center' }} variant='h2'>
                  Here’s what {name} could claim
                </Typography>
                <Stack
                  sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'custom.neutral.200',
                    borderRadius: `${radius.xl}px`,
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                  spacing={0.5}
                >
                  <Typography variant='body2' color='text.secondary'>
                    Based on what you told me, up to
                  </Typography>
                  <Typography variant='h1' color='custom.coral.700'>
                    {gbp(total)}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    a year — an estimate, not a calculation
                  </Typography>
                </Stack>
                {rows.length > 0 ? (
                  <Stack spacing={1.5}>
                    {rows.map((b) => (
                      <BenefitRow key={b.name} benefit={b} />
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    sx={{ textAlign: 'center' }}
                    variant='body2'
                    color='text.secondary'
                  >
                    Nothing landed from those answers — worth a proper chat with
                    an adviser.
                  </Typography>
                )}
              </Stack>
            </Box>
            <Box
              sx={{
                px: 3,
                pb: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'custom.neutral.200',
                bgcolor: 'background.paper',
              }}
            >
              <Button
                variant='primary'
                size='lg'
                isFullWidth
                ariaLabel='See how we collect this'
                onPress={() => setPhase('collect')}
              >
                See how we collect this
              </Button>
            </Box>
          </ScreenRoot>
        </PhoneFrame>
      </ThemeProvider>
    );
  }

  // ── Collect ──
  if (phase === 'collect') {
    return (
      <ThemeProvider theme={northStarTheme}>
        <PhoneFrame>
          <ScreenRoot>
            <StatusBar />
            <Stack
              sx={{ alignItems: 'center', px: 2, py: 1.5 }}
              direction='row'
            >
              <IconButton
                aria-label='Back'
                edge='start'
                onClick={() => setPhase('reveal')}
              >
                <Icon icon={'arrow-left' as never} fontSize={16} />
              </IconButton>
            </Stack>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
              <Collection evaluation={evaluation} recipientName={name} />
            </Box>
          </ScreenRoot>
        </PhoneFrame>
      </ThemeProvider>
    );
  }

  // ── Chat ──
  const total = totalEstimatedValue(evaluation);
  return (
    <ThemeProvider theme={northStarTheme}>
      <PhoneFrame>
        <ScreenRoot>
          <StatusBar />
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
            }}
            direction='row'
          >
            <IconButton
              aria-label='Leave chat'
              edge='start'
              onClick={() => setPhase('landing')}
            >
              <Icon icon={'xmark' as never} fontSize={16} />
            </IconButton>
            {total > 0 && (
              <Typography variant='caption' color='custom.limeGreen.800'>
                Found so far: up to {gbp(total)}/yr
              </Typography>
            )}
          </Stack>
          <Divider />

          {/* transcript */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: '16px' }}>
            <Stack spacing={1.5}>
              {messages.map((m, i) => (
                <ChatBubble
                  key={i}
                  from={m.from}
                  text={m.text}
                  onReplay={m.from === 'keni' ? () => speak(m.text) : undefined}
                />
              ))}
              <Box ref={bottomRef} />
            </Stack>
          </Box>

          {/* bottom bar: orb + quick replies */}
          <Box
            sx={{
              position: 'relative',
              px: 3,
              pb: 2,
              borderTop: 1,
              borderColor: 'custom.neutral.200',
              bgcolor: 'background.paper',
              pt: '52px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <KeniOrb size={80} state={currentId ? 'listening' : 'speaking'} />
            </Box>

            {currentId ? (
              <Stack
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
                direction='row'
                spacing={1}
                useFlexGap
              >
                {renderChips()}
              </Stack>
            ) : (
              <Button
                variant='primary'
                size='lg'
                isFullWidth
                ariaLabel={`See what ${name} could claim`}
                onPress={() => setPhase('reveal')}
              >
                See what {name} could claim
              </Button>
            )}
          </Box>
        </ScreenRoot>
      </PhoneFrame>
    </ThemeProvider>
  );
};

KeniBenefitsJourney.displayName = 'KeniBenefitsJourney';
export default KeniBenefitsJourney;
